import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { registerSwrReset } from './registry';
import { LOAD_ERROR_MESSAGE, userFacingError } from '../../lib/userFacingErrors';

/**
 * Factory for a stale-while-revalidate list store backed by localStorage.
 *
 * - Cached list renders instantly on mount/revisit/refresh.
 * - Every mount/hover/focus triggers a background refetch; concurrent calls dedupe.
 * - Cache is keyed by userId to prevent cross-user leakage on shared devices.
 * - Persisted payload is capped at `maxItems` to bound localStorage footprint.
 *
 * @param {Object} cfg
 * @param {string} cfg.name — devtools store name
 * @param {string} cfg.storageKey — localStorage key
 * @param {string} cfg.itemsField — name of the items state field (e.g. 'orgAssets')
 * @param {Object} cfg.setters — legacy-compatible setter names:
 *   { set, add, update, remove, reset }
 * @param {() => Promise<Array>} cfg.fetcher — returns the items array
 * @param {number} [cfg.maxItems=500] — cap stored and in-memory list length
 * @param {number} [cfg.cacheVersion=1] — bump to discard incompatible persisted data
 * @returns {{ useStore, useData, fetchIfStale }}
 */
export function createSwrListStore({
  name,
  storageKey,
  itemsField,
  setters,
  fetcher,
  maxItems = 500,
  cacheVersion = 1,
}) {
  const S = setters;

  const initialState = {
    [itemsField]: [],
    hasFetched: false,
    cachedForUserId: null,
    isLoading: false,
    isFetching: false,
    error: null,
  };

  const useStore = create(
    devtools(
      persist(
        (set) => ({
          ...initialState,

          [S.set]: (items) =>
            set({
              [itemsField]: (Array.isArray(items) ? items : []).slice(0, maxItems),
            }),

          [S.add]: (item) =>
            set((state) => {
              if (!item?.id) return state;
              if (state[itemsField].some((a) => a.id === item.id)) return state;
              return { [itemsField]: [item, ...state[itemsField]].slice(0, maxItems) };
            }),

          [S.update]: (id, updates) =>
            set((state) => ({
              [itemsField]: state[itemsField].map((a) =>
                a.id === id ? { ...a, ...updates } : a
              ),
            })),

          [S.remove]: (id) =>
            set((state) => ({
              [itemsField]: state[itemsField].filter((a) => a.id !== id),
            })),

          setIsLoading: (isLoading) => set({ isLoading }),
          setIsFetching: (isFetching) => set({ isFetching }),
          setHasFetched: (hasFetched) => set({ hasFetched }),
          setCachedForUserId: (cachedForUserId) => set({ cachedForUserId }),
          setError: (error) => set({ error }),

          [S.reset]: () => set(() => initialState),
        }),
        {
          name: storageKey,
          version: cacheVersion,
          storage: createJSONStorage(() => localStorage),
          migrate: () => initialState,
          partialize: (state) => ({
            [itemsField]: state[itemsField],
            hasFetched: state.hasFetched,
            cachedForUserId: state.cachedForUserId,
          }),
        }
      ),
      { name }
    )
  );

  /**
   * Imperative fetch. Always kicks off a request unless one is already in flight.
   * Invalidates cache if `userId` differs from the cached owner.
   */
  async function fetchIfStale({ userId = null, force = false } = {}) {
    const state = useStore.getState();

    if (userId && state.cachedForUserId && state.cachedForUserId !== userId) {
      state[S.reset]();
    }

    if (state.isFetching && !force) return;

    const after = useStore.getState();
    const userMatches = !userId || !after.cachedForUserId || after.cachedForUserId === userId;
    const hasCache = after.hasFetched && userMatches;

    useStore.setState({ isFetching: true, isLoading: !hasCache });

    try {
      const items = await fetcher();
      const next = useStore.getState();
      next[S.set](items);
      useStore.setState({
        hasFetched: true,
        cachedForUserId: userId || next.cachedForUserId,
        error: null,
      });
    } catch (err) {
      console.error(`[${name}] Failed to refresh list`, err);
      if (!hasCache) {
        useStore.setState({ error: userFacingError(err, LOAD_ERROR_MESSAGE) });
      }
    } finally {
      useStore.setState({ isFetching: false, isLoading: false });
    }
  }

  /**
   * Subscription hook. Treats cache as absent if `userId` doesn't match.
   */
  function useData(userId = null) {
    const items = useStore((s) => s[itemsField]);
    const hasFetched = useStore((s) => s.hasFetched);
    const isLoading = useStore((s) => s.isLoading);
    const isFetching = useStore((s) => s.isFetching);
    const error = useStore((s) => s.error);
    const cachedForUserId = useStore((s) => s.cachedForUserId);

    const userMatches = !userId || !cachedForUserId || cachedForUserId === userId;

    return {
      items: userMatches ? items : [],
      isLoading,
      isFetching,
      error,
      hasCache: hasFetched && userMatches,
    };
  }

  registerSwrReset(() => useStore.getState()[S.reset]());

  return { useStore, useData, fetchIfStale };
}
